import { useRouter, useRouterState } from '@tanstack/react-router'
import type { LucideIcon } from 'lucide-react'
import {
  createContext,
  type ReactNode,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from 'react'
import { PageActionFor, type PageActionType } from './PageAction'

/** One act a section offers on every one of its pages. */
export type SectionAction = {
  /** Stable name the page that performs it answers to. */
  key: string
  label: string
  icon?: LucideIcon
  /** Where the act sits in the header's order; most are `create`. */
  type?: PageActionType
  /** The page that opens it, when the page on screen does not. */
  to: string
}

type Registry = {
  register: (key: string, open: () => void) => void
  unregister: (key: string, open: () => void) => void
}

const SectionActionsContext = createContext<Registry | null>(null)

/**
 * A section's acts, offered from every page of it.
 *
 * The header lists what the whole section can do, not only what the page on
 * screen holds — a payment is recorded from the overview as readily as from
 * the ledger. Picking an act the page on screen performs opens it there;
 * picking one it does not goes to the page that does and opens it on arrival,
 * so one choice is both the move and the form.
 *
 * The request travels in history state rather than the address: a reload or a
 * shared link lands on the page, never on a form somebody opened once.
 */
export function SectionActions({
  actions,
  children,
}: {
  actions: SectionAction[]
  children: ReactNode
}) {
  const router = useRouter()
  const handlers = useRef(new Map<string, () => void>())
  const registry = useMemo<Registry>(
    () => ({
      register: (key, open) => {
        handlers.current.set(key, open)
      },
      unregister: (key, open) => {
        if (handlers.current.get(key) === open) handlers.current.delete(key)
      },
    }),
    [],
  )
  return (
    <SectionActionsContext.Provider value={registry}>
      {actions.map((a) => (
        <PageActionFor
          key={a.key}
          type={a.type ?? 'create'}
          label={a.label}
          icon={a.icon}
          onSelect={() => {
            const here = handlers.current.get(a.key)
            if (here) here()
            else router.navigate({ to: a.to, state: { open: a.key } })
          }}
        />
      ))}
      {children}
    </SectionActionsContext.Provider>
  )
}

/**
 * Declare that this page performs one of its section's acts.
 *
 * Picking the act here calls `open`; arriving here sent by it from another
 * page calls `open` once, and the request is then cleared so going back and
 * forward does not open it again.
 */
export function useSectionAction(key: string, open: () => void) {
  const registry = useContext(SectionActionsContext)
  const router = useRouter()
  const latest = useRef(open)
  latest.current = open
  const requested = useRouterState({
    select: (s) => s.location.state.open,
  })

  useEffect(() => {
    if (!registry) return
    const call = () => latest.current()
    registry.register(key, call)
    return () => registry.unregister(key, call)
  }, [registry, key])

  useEffect(() => {
    if (requested !== key) return
    latest.current()
    const { location } = router.state
    router.history.replace(location.href, {
      ...location.state,
      open: undefined,
    })
  }, [requested, key, router])
}
